import React, { useState, useEffect } from 'react';
import { 
  Alert, Container, Paper, Typography, Button, Dialog, TextField, MenuItem, 
  DialogTitle, DialogContent, DialogActions, Box, Grid, Card, CardContent, 
  Chip, Tabs, Tab, FormControl, InputLabel, Select, IconButton, Tooltip, InputAdornment
} from '@mui/material';
import { 
  Business, Person, TrendingUp, Group, Refresh, Add, Edit,
  Email, BusinessCenter, Close, Delete, Warning, ContentCopy, Search, Clear
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from '../utils/axios';
import PhoneInput from '../components/PhoneInput';
import { formatUserDate } from '../utils/timeFormat';
import { useAuth } from '../contexts/AuthContext';


// Company size options
const COMPANY_SIZE_OPTIONS = [
  '1-5',
  '6-25',
  '26-50',
  '51-100',
  '101-250',
  '251-500',
  '501-1000',
  '1000+'
];

const CustomerManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [phoneData, setPhoneData] = useState({ country_code: '', phone_number: '' });
  // Customer data with client-side caching
  const [allCustomers, setAllCustomers] = useState([]); // Full cached customer data
  const [customers, setCustomers] = useState([]); // Filtered display data
  const [customerSearch, setCustomerSearch] = useState(''); // Search term
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Customer users data with client-side caching
  const [allCustomerUsers, setAllCustomerUsers] = useState([]); // Full cached user data
  const [customerUsers, setCustomerUsers] = useState([]); // Filtered display data
  const [usersSearch, setUsersSearch] = useState(''); // Search term
  // Pending users data with client-side caching
  const [allPendingUsers, setAllPendingUsers] = useState([]); // Full cached pending data
  const [pendingUsers, setPendingUsers] = useState([]); // Filtered display data
  const [pendingSearch, setPendingSearch] = useState(''); // Search term
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create', 'edit', 'view'
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    domain: '',
    type: 'Corporate',
    status: 'Trial',
    subscription_tier: 'Free',
    company_size: '',
    website: '',
    phone: '',
    country_code: '',
    phone_number: '',
    linkedin_url: '',
    facebook_url: '',
    x_url: '',
    billing_email: '',
    technical_email: '',
    notes: ''
  });

  const statusColors = {
    'Trial': 'warning',
    'Active': 'success',
    'Inactive': 'default',
    'Suspended': 'error'
  };

  const tierColors = {
    'Free': 'default',
    'Premium': 'primary',
    'Enterprise': 'secondary'
  };

  const customerColumns = [
    { 
      field: 'company_name', 
      headerName: 'Company', 
      width: 200
    },
    { field: 'domain', headerName: 'Domain', width: 150 },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          icon={params.value === 'Personal' ? <Person fontSize="small" /> : <Business fontSize="small" />}
          label={params.value}
          size="small"
          variant="outlined"
          color={params.value === 'Personal' ? 'secondary' : 'primary'}
        />
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={statusColors[params.value]} 
          size="small" 
        />
      )
    },
    { 
      field: 'subscription_tier', 
      headerName: 'Tier', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={tierColors[params.value]} 
          size="small" 
          variant="outlined"
        />
      )
    },
    { field: 'total_users', headerName: 'Total Users', width: 110, type: 'number' },
    { field: 'active_users', headerName: 'Active Users', width: 120, type: 'number' },
    { field: 'pending_users', headerName: 'Pending', width: 90, type: 'number' },
    {
      field: 'last_activity',
      headerName: 'Last Activity',
      width: 150,
      valueFormatter: (params) => 
        params.value ? formatUserDate(params.value, user) : 'Never'
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (params) => formatUserDate(params.value, user)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => viewCustomer(params.row)}>
              <Group fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => editCustomer(params.row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Customer">
            <IconButton 
              size="small" 
              onClick={() => confirmDeleteCustomer(params.row)}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const userColumns = [
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'first_name', headerName: 'First Name', width: 130 },
    { field: 'last_name', headerName: 'Last Name', width: 130 },
    { field: 'role', headerName: 'Role', width: 100 },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          color={params.value ? 'success' : 'default'}
          size="small" 
        />
      )
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 150,
      valueFormatter: (params) => 
        params.value ? formatUserDate(params.value, user) : 'Never'
    }
  ];

  const pendingUserColumns = [
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'first_name', headerName: 'First Name', width: 130 },
    { field: 'last_name', headerName: 'Last Name', width: 130 },
    {
      field: 'requested_at',
      headerName: 'Requested',
      width: 150,
      valueFormatter: (params) => formatUserDate(params.value, user)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            size="small" 
            color="success" 
            onClick={() => approvePendingUser(params.row.id)}
          >
            Approve
          </Button>
          <Button 
            size="small" 
            color="error" 
            onClick={() => denyPendingUser(params.row.id)}
          >
            Deny
          </Button>
        </Box>
      )
    }
  ];

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Client-side filtering effects
  useEffect(() => {
    filterCustomers();
  }, [allCustomers, customerSearch]);

  useEffect(() => {
    filterCustomerUsers();
  }, [allCustomerUsers, usersSearch]);

  useEffect(() => {
    filterPendingUsers();
  }, [allPendingUsers, pendingSearch]);


  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/customers/customers');
      setAllCustomers(response.data || []); // Cache all customer data
      setError(null);
    } catch (err) {
      console.error('Error fetching customers:', err);
      if (err.message.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view customers.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load customers. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering functions
  const filterCustomers = () => {
    if (!allCustomers.length) {
      setCustomers([]);
      return;
    }

    let filtered = allCustomers;
    
    // Apply search filter
    if (customerSearch.trim()) {
      const searchLower = customerSearch.toLowerCase();
      filtered = filtered.filter(customer => 
        (customer.company_name && customer.company_name.toLowerCase().includes(searchLower)) ||
        (customer.domain && customer.domain.toLowerCase().includes(searchLower)) ||
        (customer.type && customer.type.toLowerCase().includes(searchLower)) ||
        (customer.status && customer.status.toLowerCase().includes(searchLower)) ||
        (customer.billing_email && customer.billing_email.toLowerCase().includes(searchLower)) ||
        (customer.technical_email && customer.technical_email.toLowerCase().includes(searchLower))
      );
    }

    setCustomers(filtered);
  };

  const filterCustomerUsers = () => {
    if (!allCustomerUsers.length) {
      setCustomerUsers([]);
      return;
    }

    let filtered = allCustomerUsers;
    
    // Apply search filter
    if (usersSearch.trim()) {
      const searchLower = usersSearch.toLowerCase();
      filtered = filtered.filter(user => 
        (user.email && user.email.toLowerCase().includes(searchLower)) ||
        (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchLower)) ||
        (user.role && user.role.toLowerCase().includes(searchLower))
      );
    }

    setCustomerUsers(filtered);
  };

  const filterPendingUsers = () => {
    if (!allPendingUsers.length) {
      setPendingUsers([]);
      return;
    }

    let filtered = allPendingUsers;
    
    // Apply search filter
    if (pendingSearch.trim()) {
      const searchLower = pendingSearch.toLowerCase();
      filtered = filtered.filter(user => 
        (user.email && user.email.toLowerCase().includes(searchLower)) ||
        (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchLower)) ||
        (user.requested_role && user.requested_role.toLowerCase().includes(searchLower))
      );
    }

    setPendingUsers(filtered);
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const [usersResponse, pendingResponse] = await Promise.all([
        axios.get(`/customers/customers/${customerId}/users`),
        axios.get(`/customers/customers/${customerId}/pending-users`)
      ]);
      setAllCustomerUsers(usersResponse.data); // Cache all user data
      setAllPendingUsers(pendingResponse.data); // Cache all pending user data
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setError('Failed to load customer details');
    }
  };

  const viewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setActiveTab(1);
    fetchCustomerDetails(customer.id);
  };

  const editCustomer = (customer) => {
    setFormData({
      company_name: customer.company_name || '',
      domain: customer.domain || '',
      type: customer.type || 'Corporate',
      status: customer.status || 'Trial',
      subscription_tier: customer.subscription_tier || 'Free',
      company_size: customer.company_size || '',
      website: customer.website || '',
      phone: customer.phone || '',
      country_code: customer.country_code || '',
      phone_number: customer.phone_number || '',
      linkedin_url: customer.linkedin_url || '',
      facebook_url: customer.facebook_url || '',
      x_url: customer.x_url || '',
      billing_email: customer.billing_email || '',
      technical_email: customer.technical_email || '',
      notes: customer.notes || ''
    });
    setPhoneData({
      country_code: customer.country_code || '',
      phone_number: customer.phone_number || ''
    });
    setValidationErrors({});
    setSelectedCustomer(customer);
    setDialogType('edit');
    setDialogOpen(true);
  };

  // Validation functions
  const validateWebsite = (url) => {
    if (!url) return url;
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  };

  const validatePhoneNumber = (phone) => {
    return phone.replace(/\D/g, ''); // Remove non-digits
  };

  const validateEmail = (email) => {
    if (!email) return { isValid: true, message: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return {
      isValid: emailRegex.test(email),
      message: emailRegex.test(email) ? '' : 'Please enter a valid email address'
    };
  };

  const createCustomer = () => {
    setFormData({
      company_name: '',
      domain: '',
      type: 'Corporate',
      status: 'Trial',
      subscription_tier: 'Free',
      company_size: '',
      website: '',
      phone: '',
      country_code: '',
      phone_number: '',
      linkedin_url: '',
      facebook_url: '',
      x_url: '',
      billing_email: '',
      technical_email: '',
      notes: ''
    });
    setPhoneData({ country_code: '', phone_number: '' });
    setValidationErrors({});
    setSelectedCustomer(null);
    setDialogType('create');
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    // Required fields validation
    if (!formData.company_name || !formData.company_name.trim()) {
      errors.company_name = 'Company name is required';
    } else if (formData.company_name.length > 255) {
      errors.company_name = 'Company name must be less than 255 characters';
    }
    
    if (!formData.domain || !formData.domain.trim()) {
      errors.domain = 'Domain is required';
    } else {
      const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const cleanDomain = formData.domain.trim().toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '');
      if (!domainPattern.test(cleanDomain)) {
        errors.domain = 'Please enter a valid domain (e.g., example.com)';
      }
    }
    
    if (!formData.type) {
      errors.type = 'Customer type is required';
    }
    
    // Optional field validation
    if (formData.company_size && !COMPANY_SIZE_OPTIONS.includes(formData.company_size)) {
      errors.company_size = 'Please select a valid company size';
    }
    
    if (formData.website && formData.website.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!urlPattern.test(formData.website)) {
        errors.website = 'Please enter a valid website URL';
      }
    }
    
    if (formData.notes && formData.notes.length > 1000) {
      errors.notes = 'Notes must be less than 1000 characters';
    }
    
    // Email validation
    const billingEmailValidation = validateEmail(formData.billing_email);
    if (!billingEmailValidation.isValid) {
      errors.billing_email = billingEmailValidation.message;
    }
    
    const technicalEmailValidation = validateEmail(formData.technical_email);
    if (!technicalEmailValidation.isValid) {
      errors.technical_email = technicalEmailValidation.message;
    }
    
    return errors;
  };

  const handleSaveCustomer = async () => {
    try {
      // Validate form
      const newValidationErrors = validateForm();
      setValidationErrors(newValidationErrors);
      
      // Don't proceed if there are validation errors
      if (Object.keys(newValidationErrors).length > 0) {
        setError('Please fix the validation errors before saving.');
        return;
      }

      // Validate and format data
      const processedData = {
        ...formData,
        website: validateWebsite(formData.website),
        country_code: phoneData.country_code,
        phone_number: validatePhoneNumber(phoneData.phone_number)
      };

      // Remove empty strings and replace with null for optional fields
      Object.keys(processedData).forEach(key => {
        if (processedData[key] === '' && !['company_name', 'domain', 'type'].includes(key)) {
          processedData[key] = null;
        }
      });
      
      let response;
      if (dialogType === 'create') {
        response = await axios.post('/customers/customers', processedData);
        // Update formData with the created customer data, preserving form values if response missing them
        const updatedCustomer = response.data;
        setFormData(prevFormData => ({
          company_name: updatedCustomer.company_name || prevFormData.company_name || '',
          domain: updatedCustomer.domain || prevFormData.domain || '',
          type: updatedCustomer.type || prevFormData.type || 'Corporate',
          status: updatedCustomer.status || prevFormData.status || 'Trial',
          subscription_tier: updatedCustomer.subscription_tier || prevFormData.subscription_tier || 'Free',
          company_size: updatedCustomer.company_size || prevFormData.company_size || '',
          website: updatedCustomer.website || prevFormData.website || '',
          phone: updatedCustomer.phone || prevFormData.phone || '',
          country_code: updatedCustomer.country_code || prevFormData.country_code || '',
          phone_number: updatedCustomer.phone_number || prevFormData.phone_number || '',
          linkedin_url: updatedCustomer.linkedin_url || prevFormData.linkedin_url || '',
          facebook_url: updatedCustomer.facebook_url || prevFormData.facebook_url || '',
          x_url: updatedCustomer.x_url || prevFormData.x_url || '',
          billing_email: updatedCustomer.billing_email || prevFormData.billing_email || '',
          technical_email: updatedCustomer.technical_email || prevFormData.technical_email || '',
          notes: updatedCustomer.notes || prevFormData.notes || ''
        }));
        setPhoneData({
          country_code: updatedCustomer.country_code || '',
          phone_number: updatedCustomer.phone_number || ''
        });
        // Switch to edit mode
        setDialogType('edit');
        setSelectedCustomer(updatedCustomer);
      } else {
        response = await axios.put(`/customers/customers/${selectedCustomer.id}`, processedData);
        
        // Update formData with the response data, preserving current values if response is missing them
        const updatedCustomer = response.data;
        
        setFormData(prevFormData => ({
          company_name: updatedCustomer.company_name || prevFormData.company_name || '',
          domain: updatedCustomer.domain || prevFormData.domain || '',
          type: updatedCustomer.type || prevFormData.type || 'Corporate',
          status: updatedCustomer.status || prevFormData.status || 'Trial',
          subscription_tier: updatedCustomer.subscription_tier || prevFormData.subscription_tier || 'Free',
          company_size: updatedCustomer.company_size || prevFormData.company_size || '',
          website: updatedCustomer.website || prevFormData.website || '',
          phone: updatedCustomer.phone || prevFormData.phone || '',
          country_code: updatedCustomer.country_code || prevFormData.country_code || '',
          phone_number: updatedCustomer.phone_number || prevFormData.phone_number || '',
          linkedin_url: updatedCustomer.linkedin_url || prevFormData.linkedin_url || '',
          facebook_url: updatedCustomer.facebook_url || prevFormData.facebook_url || '',
          x_url: updatedCustomer.x_url || prevFormData.x_url || '',
          billing_email: updatedCustomer.billing_email || prevFormData.billing_email || '',
          technical_email: updatedCustomer.technical_email || prevFormData.technical_email || '',
          notes: updatedCustomer.notes || prevFormData.notes || ''
        }));
        setPhoneData({
          country_code: updatedCustomer.country_code || '',
          phone_number: updatedCustomer.phone_number || ''
        });
        setSelectedCustomer(updatedCustomer);
      }
      
      // Refresh the customer list but keep dialog open
      await fetchCustomers();
      setError(null);
      
      // Show success message
      setSuccessMessage(`Customer ${dialogType === 'create' ? 'created' : 'updated'} successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving customer:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to save customer');
    }
  };

  const approvePendingUser = async (userId) => {
    try {
      await axios.post(`/customers/customers/${selectedCustomer.id}/pending-users/${userId}/approve`);
      fetchCustomerDetails(selectedCustomer.id);
      setError(null);
    } catch (err) {
      console.error('Error approving user:', err);
      setError('Failed to approve user');
    }
  };

  const denyPendingUser = async (userId) => {
    try {
      await axios.post(`/customers/customers/${selectedCustomer.id}/pending-users/${userId}/deny`);
      fetchCustomerDetails(selectedCustomer.id);
      setError(null);
    } catch (err) {
      console.error('Error denying user:', err);
      setError('Failed to deny user');
    }
  };

  const refreshStats = async (customerId) => {
    try {
      await axios.post(`/customers/customers/${customerId}/refresh-stats`);
      fetchCustomers();
      if (selectedCustomer?.id === customerId) {
        fetchCustomerDetails(customerId);
      }
    } catch (err) {
      console.error('Error refreshing stats:', err);
      setError('Failed to refresh statistics');
    }
  };

  const confirmDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setDeleteConfirmName('');
    setDeleteConfirmOpen(true);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCustomerToDelete(null);
    setDeleteConfirmName('');
  };

  const executeDelete = async () => {
    if (!customerToDelete || deleteConfirmName !== customerToDelete.company_name) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`/customers/customers/${customerToDelete.id}`);
      
      // Show success message with deletion details
      const summary = response.data.deletion_summary;
      let summaryText = `Customer deleted successfully. `;
      if (summary.deleted_users > 0) {
        summaryText += `${summary.deleted_users} users, `;
      }
      if (summary.deleted_pending_users > 0) {
        summaryText += `${summary.deleted_pending_users} pending users, `;
      }
      summaryText += `and all related data removed.`;
      
      setSuccessMessage(summaryText);
      setTimeout(() => setSuccessMessage(null), 5000);
      
      // Refresh customer list
      await fetchCustomers();
      
      // Close delete dialog
      cancelDelete();
      setError(null);
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(err.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  const renderCustomerOverview = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Customer Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={createCustomer}
        >
          Add Customer
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search customers..."
          value={customerSearch}
          onChange={(e) => setCustomerSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: customerSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setCustomerSearch('')}
                  edge="end"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={customers}
          columns={customerColumns}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={loading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          autoHeight
        />
      </Paper>
    </Box>
  );

  const renderCustomerDetails = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">{selectedCustomer?.company_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedCustomer?.domain} • {selectedCustomer?.type}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refreshStats(selectedCustomer?.id)}
          >
            Refresh Stats
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => editCustomer(selectedCustomer)}
          >
            Edit Customer
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Group color="primary" />
                <Box>
                  <Typography variant="h6">{selectedCustomer?.total_users}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Users</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUp color="success" />
                <Box>
                  <Typography variant="h6">{selectedCustomer?.active_users}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Users</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Email color="warning" />
                <Box>
                  <Typography variant="h6">{selectedCustomer?.pending_users}</Typography>
                  <Typography variant="body2" color="text.secondary">Pending Requests</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessCenter color="info" />
                <Box>
                  <Typography variant="h6">
                    {selectedCustomer?.current_seats || 0} / {selectedCustomer?.seats_limit || 'Unlimited'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current / Licensed Seats
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {selectedCustomer?.seats_limit ? 
                      Math.max(0, selectedCustomer.seats_limit - (selectedCustomer?.current_seats || 0)) + ' free seats' : 
                      'Unlimited seats'
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status and Tier Information */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Subscription Details</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Chip 
                  label={selectedCustomer?.status} 
                  color={statusColors[selectedCustomer?.status]} 
                  size="small"
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Tier:</Typography>
                <Chip 
                  label={selectedCustomer?.subscription_tier} 
                  color={tierColors[selectedCustomer?.subscription_tier]} 
                  size="small"
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
              {selectedCustomer?.company_size && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Size:</Typography>
                  <Chip 
                    label={`${selectedCustomer.company_size} employees`} 
                    variant="outlined"
                    size="small"
                    color="info"
                    sx={{ fontWeight: 'medium' }}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Users</Typography>
          <TextField
            size="small"
            placeholder="Search users..."
            value={usersSearch}
            onChange={(e) => setUsersSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: usersSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setUsersSearch('')}
                    edge="end"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>
        <Paper sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={customerUsers}
            columns={userColumns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Paper>
      </Box>

      {(pendingUsers.length > 0 || pendingSearch.trim()) && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Pending User Requests</Typography>
            <TextField
              size="small"
              placeholder="Search pending users..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: pendingSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setPendingSearch('')}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />
          </Box>
          <Paper sx={{ height: 300, width: '100%' }}>
            <DataGrid
              rows={pendingUsers}
              columns={pendingUserColumns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="All Customers" />
        <Tab label="Customer Details" disabled={!selectedCustomer} />
      </Tabs>

      {activeTab === 0 && renderCustomerOverview()}
      {activeTab === 1 && selectedCustomer && renderCustomerDetails()}

      <Dialog 
        open={dialogOpen} 
        onClose={(event, reason) => {
          // Only close on ESC key, not on backdrop click
          if (reason !== 'backdropClick') {
            setDialogOpen(false);
          }
        }}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pr: 1
        }}>
          {dialogType === 'create' ? 'Create Customer' : 'Edit Customer'}
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflowY: 'auto', pb: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Company Name"
                value={formData.company_name}
                onChange={(e) => {
                  setFormData({...formData, company_name: e.target.value});
                  if (validationErrors.company_name) {
                    setValidationErrors({...validationErrors, company_name: ''});
                  }
                }}
                disabled={dialogType === 'edit'}
                error={!!validationErrors.company_name}
                helperText={validationErrors.company_name || (dialogType === 'edit' ? 'Company name cannot be changed' : '')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Domain"
                value={formData.domain}
                onChange={(e) => {
                  setFormData({...formData, domain: e.target.value});
                  if (validationErrors.domain) {
                    setValidationErrors({...validationErrors, domain: ''});
                  }
                }}
                disabled={dialogType === 'edit'}
                error={!!validationErrors.domain}
                helperText={validationErrors.domain || (dialogType === 'edit' ? 'Domain cannot be changed' : 'e.g., example.com')}
                placeholder="example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={dialogType === 'edit'} error={!!validationErrors.type} required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => {
                    setFormData({...formData, type: e.target.value});
                    if (validationErrors.type) {
                      setValidationErrors({...validationErrors, type: ''});
                    }
                  }}
                  label="Type"
                >
                  <MenuItem value="Corporate">Corporate</MenuItem>
                  <MenuItem value="Personal">Personal</MenuItem>
                </Select>
                {validationErrors.type && (
                  <Box component="span" sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5, ml: 1.75 }}>
                    {validationErrors.type}
                  </Box>
                )}
                {dialogType === 'edit' && !validationErrors.type && (
                  <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5, ml: 1.75 }}>
                    Type cannot be changed
                  </Box>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="Trial">Trial</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subscription Tier</InputLabel>
                <Select
                  value={formData.subscription_tier}
                  onChange={(e) => setFormData({...formData, subscription_tier: e.target.value})}
                  label="Subscription Tier"
                >
                  <MenuItem value="Free">Free</MenuItem>
                  <MenuItem value="Premium">Premium</MenuItem>
                  <MenuItem value="Enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Company-specific fields */}
            {formData.type === 'Corporate' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Company Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Company Size</InputLabel>
                    <Select
                      value={formData.company_size || ''}
                      onChange={(e) => setFormData({...formData, company_size: e.target.value})}
                      label="Company Size"
                    >
                      {COMPANY_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size} employees
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.website}
                    onChange={(e) => {
                      setFormData({...formData, website: e.target.value});
                      if (validationErrors.website) {
                        setValidationErrors({...validationErrors, website: ''});
                      }
                    }}
                    error={!!validationErrors.website}
                    helperText={validationErrors.website || 'Optional'}
                    placeholder="https://example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Billing Email"
                    type="email"
                    value={formData.billing_email}
                    placeholder={formData.type === 'Corporate' && formData.domain ? `example@${formData.domain}` : ''}
                    onChange={(e) => {
                      setFormData({...formData, billing_email: e.target.value});
                      // Clear validation error when user starts typing
                      if (validationErrors.billing_email) {
                        setValidationErrors({...validationErrors, billing_email: ''});
                      }
                    }}
                    error={!!validationErrors.billing_email}
                    helperText={validationErrors.billing_email || (formData.type === 'Corporate' && formData.domain ? `Must use @${formData.domain}` : '')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Technical Email"
                    type="email"
                    value={formData.technical_email}
                    placeholder={formData.type === 'Corporate' && formData.domain ? `tech@${formData.domain}` : ''}
                    onChange={(e) => {
                      setFormData({...formData, technical_email: e.target.value});
                      // Clear validation error when user starts typing
                      if (validationErrors.technical_email) {
                        setValidationErrors({...validationErrors, technical_email: ''});
                      }
                    }}
                    error={!!validationErrors.technical_email}
                    helperText={validationErrors.technical_email || (formData.type === 'Corporate' && formData.domain ? `Must use @${formData.domain}` : '')}
                  />
                </Grid>
              </>
            )}

            {/* Contact Information - Show for all customers */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Contact Information</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Phone Number
                </Typography>
                <Box sx={{ 
                  '& .react-tel-input': {
                    fontFamily: 'inherit'
                  },
                  '& .react-tel-input .form-control': {
                    width: '100%',
                    height: '56px',
                    padding: '16.5px 14px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: 'inherit',
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.87)'
                    },
                    '&:focus': {
                      borderColor: '#1976d2',
                      borderWidth: '2px',
                      outline: 'none'
                    }
                  },
                  '& .react-tel-input .flag-dropdown': {
                    backgroundColor: 'transparent',
                    border: 'none'
                  },
                  '& .react-tel-input .selected-flag': {
                    backgroundColor: 'transparent',
                    '&:hover, &:focus': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }
                }}>
                  <PhoneInput
                    value={`${phoneData.country_code}${phoneData.phone_number}`}
                    onChange={(data) => {
                      setPhoneData(data);
                      setFormData({
                        ...formData,
                        country_code: data.country_code,
                        phone_number: data.phone_number
                      });
                    }}
                    disabled={false}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Social Media - Only show for Corporate customers */}
            {formData.type === 'Corporate' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Social Media</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="LinkedIn URL"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                    placeholder="https://linkedin.com/company/..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Facebook URL"
                    value={formData.facebook_url}
                    onChange={(e) => setFormData({...formData, facebook_url: e.target.value})}
                    placeholder="https://facebook.com/..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="X (Twitter) URL"
                    value={formData.x_url}
                    onChange={(e) => setFormData({...formData, x_url: e.target.value})}
                    placeholder="https://x.com/..."
                  />
                </Grid>
              </>
            )}

            {/* Personal Social Media - Simplified for Personal customers */}
            {formData.type === 'Personal' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Social Media</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="LinkedIn Profile"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                    placeholder="https://linkedin.com/in/..."
                  />
                </Grid>
              </>
            )}

            {/* Notes */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Additional Information</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => {
                  setFormData({...formData, notes: e.target.value});
                  if (validationErrors.notes) {
                    setValidationErrors({...validationErrors, notes: ''});
                  }
                }}
                error={!!validationErrors.notes}
                helperText={validationErrors.notes || `${formData.notes.length}/1000 characters`}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          flexShrink: 0, 
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCustomer} variant="contained">
            {dialogType === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={(event, reason) => {
          if (reason === 'escapeKeyDown') {
            cancelDelete();
          }
          // Prevent closing on backdrop click
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Delete Customer
          </Box>
          <IconButton
            onClick={cancelDelete}
            size="small"
            sx={{ color: 'grey.500' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {customerToDelete && (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>This action cannot be undone!</strong>
                </Typography>
                <Typography variant="body2">
                  This will permanently delete the customer "{customerToDelete.company_name}" and ALL related data including:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  <li>All users ({customerToDelete.total_users || 0})</li>
                  <li>All pending user requests ({customerToDelete.pending_users || 0})</li>
                  <li>All user actions and preferences</li>
                  <li>All billing records</li>
                  <li>All feature access records</li>
                  <li>All social media posts</li>
                  <li>All data sources</li>
                </Box>
              </Alert>

              <Typography variant="body1" sx={{ mb: 2 }}>
                To confirm deletion, please type the customer name exactly as shown:
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 2, 
                p: 2, 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body1" sx={{ 
                  fontFamily: 'monospace', 
                  flexGrow: 1,
                  color: 'text.primary',
                  fontWeight: 500
                }}>
                  {customerToDelete.company_name}
                </Typography>
                <Tooltip title="Copy company name">
                  <IconButton 
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(customerToDelete.company_name);
                      // You could add a snackbar here to show copy success
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                label="Enter customer name to confirm"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={customerToDelete.company_name}
                error={deleteConfirmName && deleteConfirmName !== customerToDelete.company_name}
                helperText={deleteConfirmName && deleteConfirmName !== customerToDelete.company_name ? 'Customer name does not match' : ''}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>
            Cancel
          </Button>
          <Button 
            onClick={executeDelete}
            variant="contained"
            color="error"
            disabled={!customerToDelete || deleteConfirmName !== customerToDelete.company_name || loading}
            startIcon={<Delete />}
          >
            {loading ? 'Deleting...' : 'Delete Customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomerManagement;